import { useCallback, useEffect, useState } from 'react';
import { blogCandidatesService, BlogCandidate } from '../../../../services/blogCandidatesService';
import { blogRunsService, BlogRun } from '../../../../services/blogRunsService';
import type { ArticleRow, ArticleStatus } from './articleTypes';

export function useArticles(showPublishedArticles: boolean) {
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articlesError, setArticlesError] = useState<string | null>(null);

  const computeArticleStatus = useCallback((run?: BlogRun | null, candidate?: BlogCandidate | null): ArticleStatus => {
    if (candidate?.published_confirmed_at) return 'Published';
    if (!run || (!run.current_stage_id && (!run.stages || run.stages.length === 0))) return 'Not Started';
    if (run.status === 'succeeded') return 'Published';
    return 'In progress';
  }, []);

  const loadArticles = useCallback(async () => {
    setArticlesLoading(true);
    setArticlesError(null);
    try {
      const candidateResp = await blogCandidatesService.list();
      const candidates = (candidateResp.candidates || []).filter((c) => {
        if (c.status === 'archived') return false;
        if (!showPublishedArticles && c.published_confirmed_at) return false;
        return true;
      });
      const rows = await Promise.all(candidates.map(async (candidate) => {
        const runsResp = await blogRunsService.list(candidate.candidate_id);
        const run = runsResp.runs?.[0] || null;
        const status = computeArticleStatus(run, candidate);
        const completedStages = (run?.stages || []).filter(
          (s: { status: string }) => s.status === 'succeeded'
        );
        const currentStageId = completedStages.length > 0
          ? completedStages[completedStages.length - 1].stage_id
          : run?.current_stage_id || null;
        return { candidate, run, status, currentStageId };
      }));
      setArticles(rows);
    } catch (e: any) {
      setArticlesError(e?.message || 'Failed to load articles');
    } finally {
      setArticlesLoading(false);
    }
  }, [computeArticleStatus, showPublishedArticles]);

  useEffect(() => {
    void loadArticles();
  }, [loadArticles]);

  return { articles, articlesLoading, articlesError, setArticlesError, loadArticles };
}
